/**
 * Sprint 7a (ICD v0.2 section 9, ADR 0012, ADR 0017): battery and lift data of one vehicle, as the
 * vehicle page shows it. Shared by the Supabase and demo providers. The device is authoritative: values
 * are what it reported; a load the cloud had to estimate is flagged `estimated`.
 */
import { moduleConfigFields, type BatteryType, type ChargeEventRecord, type DeviceModule, type ModuleSettings } from "@fts/core";

export type BatteryReading = {
  soc: number | null;
  batV: number | null;
  /** + charge, - discharge (A), ICD v0.2 section 9. */
  batA: number | null;
  batT: number | null;
  /** Effective device time of the reading. */
  at: string | null;
};

export type LiftRecord = {
  id: string;
  at: string;
  peakBar: number | null;
  maxForkMm: number | null;
  durMs: number | null;
  /** The device's load_kg, or the cloud's fallback estimate (`estimated`); null = neither. */
  loadKg: number | null;
  estimated: boolean;
  /** The device reported an `overload` for this cycle. */
  overload: boolean;
};

/** Per-vehicle settings: the first four are pushed to the device by config; the rest stay in the cloud. */
export type VehicleModuleSettings = ModuleSettings & {
  /** Cloud-only tare of the fallback load estimate. */
  liftEmptyBar: number | null;
  ratedCapacityKg: number | null;
};

export const CONFIG_DELIVERY_STATES = ["none", "kept", "pending", "acked", "failed"] as const;
/**
 * none = nothing set and nothing ever sent; kept = nothing set now, but the device keeps the values it was
 * sent earlier (never claim "nothing set" then); pending = not confirmed yet; acked = the device holds it;
 * failed = refused by the device or not delivered (no automatic resend until something changes).
 */
export type ConfigDelivery = { state: (typeof CONFIG_DELIVERY_STATES)[number]; at: string | null };

export type VehicleModuleData = {
  deviceId: string | null;
  /** Announced in the device's last hello; null = never reported (older firmware or no device). */
  modules: DeviceModule[] | null;
  battery: BatteryReading | null;
  /** Average SoC per 10 minutes over the last 24 h, oldest first. */
  socSeries: { at: string; soc: number }[];
  /** charge_start / charge_end of the last 7 days (core pairChargeSessions pairs them). */
  chargeEvents: ChargeEventRecord[];
  /** lift events, newest first, at most 10. */
  lifts: LiftRecord[];
  /** Turkey local day: lift events and (separate) overload events. */
  liftsToday: number;
  overloadsToday: number;
  settings: VehicleModuleSettings;
  delivery: ConfigDelivery;
  /** Settings already sent to the device: they can be changed, not cleared (the device keeps its last value). */
  lockedSettings: SettingKey[];
  /** Newest deep_discharge event of the vehicle (the device decides; the cloud knows no threshold). */
  lastDeepDischargeAt: string | null;
};

export type SettingKey = keyof ModuleSettings;
const CONFIG_KEY: Record<string, SettingKey> = { bat_type: "batType", bat_ah: "batAh", lift_kg_per_bar: "liftKgPerBar", lift_bar_threshold: "liftBarThreshold" };

/** device_module_config row as the console reads it. */
export type ConfigRow = {
  config: Record<string, unknown>;
  ver: number;
  sent_ver: number | null;
  acked_ver: number | null;
  acked_at: string | null;
  sent_at: string | null;
  rejected_ver?: number | null;
  error: string | null;
  /** The device was re-paired after receiving this config and may still hold it. */
  inherited?: boolean;
};

/** Settings that were sent at least once (the DB guard fts_module_settings_guard uses the same rule). */
export function lockedSettings(row: ConfigRow | null): SettingKey[] {
  if (!row || row.sent_ver === null) return [];
  return Object.keys(row.config).map((k) => CONFIG_KEY[k]).filter((k): k is SettingKey => !!k);
}

export type ModuleSettingsInput = { vehicleId: string } & VehicleModuleSettings;

export const EMPTY_SETTINGS: VehicleModuleSettings = { batType: null, batAh: null, liftKgPerBar: null, liftBarThreshold: null, liftEmptyBar: null, ratedCapacityKg: null };

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);

type EventRow = { id: number | string; ts: string; type: string; module: Record<string, unknown> | null };

/** device_events lift row -> record. `overloads`: overload event times, to flag the cycle they belong to. */
export function toLiftRecord(r: EventRow, overloads: readonly string[] = []): LiftRecord {
  const m = r.module ?? {};
  const at = Date.parse(r.ts);
  const dur = num(m.dur_ms);
  // The device sends `overload` at the peak, `lift` when the pressure drops: match within the cycle (±2 s).
  const from = at - (dur ?? 0) - 2000;
  const overload = overloads.some((o) => {
    const t = Date.parse(o);
    return Number.isFinite(t) && t >= from && t <= at + 2000;
  });
  return { id: String(r.id), at: r.ts, peakBar: num(m.peak_bar), maxForkMm: num(m.max_fork_mm), durMs: dur, loadKg: num(m.load_kg), estimated: m.load_estimated === true, overload };
}

/** device_events row -> charge event for pairChargeSessions. */
export function toChargeEvent(r: Omit<EventRow, "id">): ChargeEventRecord {
  const m = r.module ?? {};
  return { type: r.type === "charge_end" ? "charge_end" : "charge_start", at: r.ts, soc: num(m.soc), ah: num(m.ah), kwh: num(m.kwh) };
}

const stable = (o: Record<string, unknown>) => JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k]]));

/** Delivery state of the settings to the device (device_module_config row, null = none yet). */
export function configDelivery(settings: ModuleSettings, row: ConfigRow | null): ConfigDelivery {
  const want = moduleConfigFields(settings);
  if (Object.keys(want).length === 0)
    return row && (row.sent_ver !== null || row.inherited) ? { state: "kept", at: row.acked_at ?? row.sent_at } : { state: "none", at: null };
  // Saved but not picked up yet (the API sends within a minute), or changed since.
  if (!row || stable(row.config) !== stable(want)) return { state: "pending", at: null };
  if (row.acked_ver === row.ver) return { state: "acked", at: row.acked_at };
  if (row.error || row.rejected_ver === row.ver) return { state: "failed", at: row.sent_at };
  return { state: "pending", at: row.sent_at };
}

export const hasModule = (d: Pick<VehicleModuleData, "modules"> | null, type: DeviceModule["type"]): boolean => !!d?.modules?.some((m) => m.type === type);

export type { BatteryType };
